import { Link } from 'react-router-dom';
import type { NewsArticle } from '../../../../mocks/news';
import { categoryBadgeClass } from '../../../../services/news';
import { useTranslation } from 'react-i18next';
import ShareButtons from '../../../../components/common/ShareButtons';

interface ArticleHeaderProps {
  article: NewsArticle;
}

export default function ArticleHeader({ article }: ArticleHeaderProps) {
  const { t } = useTranslation();
  const badgeClass = categoryBadgeClass(article.category);

  return (
    <section className="relative bg-white pt-[72px] overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <img src={article.image} alt={article.title} className="w-full h-full object-cover object-top opacity-10" />
        <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-white/60 to-white" />
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-transparent to-white/95" />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 py-20">
        <div className="flex flex-wrap items-center gap-2 mb-10 text-xs text-gray-400">
          <Link to="/" className="hover:text-primary transition-colors">{t('breadcrumb.home')}</Link>
          <i className="ri-arrow-right-s-line text-gray-300" />
          <Link to="/news" className="hover:text-primary transition-colors">{t('breadcrumb.news')}</Link>
          <i className="ri-arrow-right-s-line text-gray-300" />
          <span className="text-gray-500 line-clamp-1 max-w-[300px]">{article.title}</span>
        </div>

        <div className="max-w-3xl">
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span className={`px-3 py-1 text-xs font-medium rounded-full border ${badgeClass}`}>{article.categoryLabel}</span>
            <div className="flex items-center gap-1.5 text-gray-400 text-sm">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-line text-sm" /></div>
              {article.date}
            </div>
            <div className="flex items-center gap-1.5 text-gray-400 text-sm">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-time-line text-sm" /></div>
              {t('newsDetail.readTime', { time: article.readTime })}
            </div>
          </div>

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-black text-gray-900 leading-tight mb-6">
            {article.title}
          </h1>

          <p className="text-gray-600 text-lg leading-relaxed mb-8 border-l-2 border-primary pl-5">
            {article.summary}
          </p>

          <div className="flex flex-wrap gap-2 mb-10">
            {article.tags.map((tag) => (
              <span key={tag} className="px-3 py-1 bg-light-alt border border-primary/10 text-gray-400 text-xs rounded-md"># {tag}</span>
            ))}
          </div>

          <div className="flex items-center gap-4 p-4 bg-light-alt border border-primary/10 rounded-xl">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary/10 border border-primary/20 shrink-0">
              <i className="ri-user-line text-primary text-base" />
            </div>
            <div>
              <div className="text-sm font-medium text-gray-800">{article.author}</div>
              <div className="text-xs text-gray-400">{article.authorRole}</div>
            </div>
            <div className="ml-auto flex items-center gap-3">
              <span className="text-xs text-gray-300">{t('newsDetail.share')}</span>
              <ShareButtons
                variant="header"
                channels={['twitter', 'linkedin', 'copy']}
                payload={{ title: article.title, text: article.summary }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent pointer-events-none" />
    </section>
  );
}
